import ReportSectionCard from './shared/ReportSectionCard.component'
import ReportSectionHeading from '@components/pages/Reports/shared/ReportSectionHeading.component'
import React, { useMemo, useState } from 'react'
import ReportViewTabs from '@components/pages/Reports/shared/ReportViewTabs.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import ReportBundleStickyTopWhenExpanded from '@components/pages/Reports/shared/ReportBundleStickyTopWhenExpanded.component'
import ReportFilterToolbar from '@components/pages/Reports/shared/ReportFilterToolbar.component'
import ReportKpiGrid from '@components/pages/Reports/shared/ReportKpiGrid.component'
import ReportPageHeader from '@components/pages/Reports/shared/ReportPageHeader.component'
import { REPORTS_DISTRIBUTION_VIEW_TABS } from '@constants/Reports/patientBaseHealth.constants'
import {
  CHAIR_ROOM_UTILIZATION_FILTERS,
  CHAIR_ROOM_UTILIZATION_KPI_DEFINITIONS,
} from '@constants/Reports/chairRoomUtilization.constants'
import {
  ReportsDistributionView,
  ReportsKpiMetric,
} from '@dataTypes/reports.type'
import { useChairRoomUtilizationReport } from '@hooks/reports/useChairRoomUtilizationReport'
import { useReportFinanceUiEffects } from '@hooks/reports/useReportFinanceUiEffects'
import {
  hasNonZeroDashboardValues,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import {
  computeDynamicCountAxis,
  computePercentAxis,
  formatKpiPercent,
} from '@utils/reports/revenueRealizationFinance.util'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from '@components/pages/Reports/shared/ReportDesignSystemCharts.component'

const REPORT_CHART_AXIS_TICK = { fontSize: 12, fill: 'var(--gray-500)' }
const REPORT_CHART_TOOLTIP_STYLE = {
  borderRadius: 8,
  border: '1px solid var(--gray-200)',
  fontSize: 12,
}
const REPORT_CHART_GRID_STROKE = 'var(--gray-100)'
const REPORT_BAR_CHART_MARGIN = { top: 24, right: 12, left: 4, bottom: 0 }
const REPORT_BAR_CHART_MARGIN_LEGEND = {
  top: 24,
  right: 12,
  left: 4,
  bottom: 12,
}
const REPORT_LEGEND_WRAPPER_STYLE = {
  fontSize: 12,
  paddingTop: 0,
  lineHeight: '16px',
}
const REPORT_BAR_CATEGORY_GAP = '16%'
const REPORT_GROUPED_BAR_GAP = 4
const REPORT_YAXIS_WIDTH = 46

function formatMinutes(value: number): string {
  if (value >= 1000) {
    const thousands = value / 1000
    const trimmed = Number.isInteger(thousands)
      ? thousands.toFixed(0)
      : thousands.toFixed(1)
    return `${trimmed}K min`
  }
  return `${Math.round(value)} min`
}

function formatMinutesAxis(value: number): string {
  if (value === 0) return '0 Min'
  if (value >= 1000) return `${Math.round(value / 1000)}K Min`
  return `${Math.round(value)} Min`
}

function formatKpiNumber(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return '—'
  return Math.round(value).toLocaleString('en-US')
}

function formatKpiDecimal(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return '—'
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })
}

function ChairRoomUtilizationReport(): React.ReactElement {
  const [chairTimeView, setChairTimeView] =
    useState<ReportsDistributionView>('Count')
  const [roomUtilView, setRoomUtilView] =
    useState<ReportsDistributionView>('Count')
  const [providersView, setProvidersView] =
    useState<ReportsDistributionView>('Count')
  const [reportSaveViewAction, setReportSaveViewAction] =
    useState<JSX.Element | null>(null)
  const [reportGeneratePDFAction, setReportGeneratePDFAction] =
    useState<JSX.Element | null>(null)
  // `reportFiltersAndMetrics` is the source of truth for the filters and metrics of the currently selected report view.
  // Use this for all data queries and any logic that depends on the selected report view.
  const [reportFiltersAndMetrics, setReportFiltersAndMetrics] = useState<{
    filters: Record<string, boolean>
    metrics: Record<string, boolean>
  } | null>(null)
  const { loading, error, data, bucketLabel } = useChairRoomUtilizationReport(
    (reportFiltersAndMetrics?.filters ?? {}) as unknown as Record<
      string,
      string
    >,
  )
  useReportFinanceUiEffects({
    loading,
    error,
    errorMessage:
      'Failed to load chair and room utilization data. Please try again.',
  })

  const kpiItems = useMemo((): ReportsKpiMetric[] => {
    if (!data) {
      return CHAIR_ROOM_UTILIZATION_KPI_DEFINITIONS.map((def) => ({
        id: def.id,
        title: def.title,
        footer: def.footer,
        value: loading ? '…' : '—',
      }))
    }

    const valueById: Record<string, number> = {
      'chair-time-used': data.chairTimeUsed.value,
      'chair-rooms-used': data.chairTimeAvailable.value,
      'avg-rooms-used': data.avgRoomsUsed.value,
      'avg-room-operations': data.avgRoomOperations.value,
      'utilization-rate': data.utilizationRate.value,
    }

    return CHAIR_ROOM_UTILIZATION_KPI_DEFINITIONS.map((def) => {
      const raw = valueById[def.id]
      let value: string
      if (def.format === 'percent') {
        value = formatKpiPercent(raw)
      } else if (def.format === 'decimal') {
        value = formatKpiDecimal(raw)
      } else {
        value = formatKpiNumber(raw)
      }
      return {
        id: def.id,
        title: def.title,
        footer: def.footer,
        value,
      }
    })
  }, [data, loading])

  const chairTimeChartData = useMemo(() => {
    if (!data) return []
    const availableByLabel = new Map(
      data.chairTimeAvailableByPeriod.buckets.map((b) => [b.label, b.value]),
    )
    const rows = data.chairTimeUsedByPeriod.buckets.map((row) => ({
      month: row.label,
      used: row.value,
      available: availableByLabel.get(row.label) ?? 0,
    }))
    return rows.map((row) => {
      if (chairTimeView === 'Count') {
        return {
          month: row.month,
          usedValue: row.used,
          availableValue: row.available,
          usedLabel: formatMinutes(row.used),
          availableLabel: formatMinutes(row.available),
        }
      }
      const total = row.used + row.available || 1
      const usedPct = Math.round((row.used / total) * 1000) / 10
      const availablePct = Math.round((row.available / total) * 1000) / 10
      return {
        month: row.month,
        usedValue: usedPct,
        availableValue: availablePct,
        usedLabel: `${usedPct}%`,
        availableLabel: `${availablePct}%`,
      }
    })
  }, [data, chairTimeView])

  const chairTimeYAxis = useMemo(() => {
    if (chairTimeView === 'Percentage') {
      return {
        domain: [0, 100] as [number, number],
        ticks: [0, 25, 50, 75, 100],
        tickFormatter: (v: number) => `${v}%`,
      }
    }
    const values = chairTimeChartData.flatMap((row) => [
      row.usedValue,
      row.availableValue,
    ])
    const axis = computeDynamicCountAxis(values)
    return {
      ...axis,
      tickFormatter: formatMinutesAxis,
    }
  }, [chairTimeView, chairTimeChartData])

  const roomUtilChartData = useMemo(() => {
    if (!data) return []
    const availableByLabel = new Map(
      data.roomsAvailableByPeriod.buckets.map((b) => [b.label, b.value]),
    )
    const rows = data.roomsUsedByPeriod.buckets.map((row) => ({
      month: row.label,
      used: row.value,
      available: availableByLabel.get(row.label) ?? 0,
    }))
    return rows.map((row) => {
      if (roomUtilView === 'Count') {
        return {
          month: row.month,
          usedValue: row.used,
          availableValue: row.available,
          usedLabel: row.used.toFixed(1),
          availableLabel: row.available.toFixed(1),
        }
      }
      const usedPct =
        row.available > 0
          ? Math.round((row.used / row.available) * 1000) / 10
          : 0
      return {
        month: row.month,
        usedValue: usedPct,
        availableValue: 100,
        usedLabel: `${usedPct}%`,
        availableLabel: '100%',
      }
    })
  }, [data, roomUtilView])

  const roomUtilYAxis = useMemo(() => {
    if (roomUtilView === 'Percentage') {
      return {
        domain: [0, 100] as [number, number],
        ticks: [0, 25, 50, 75, 100],
        tickFormatter: (v: number) => `${v}%`,
      }
    }
    return computeDynamicCountAxis(
      roomUtilChartData.flatMap((row) => [row.usedValue, row.availableValue]),
    )
  }, [roomUtilView, roomUtilChartData])

  const providersTotal =
    data?.providersWorkingByPeriod.buckets.reduce(
      (sum, row) => sum + row.value,
      0,
    ) ?? 0

  const providersChartData = useMemo(() => {
    if (!data) return []
    const total = providersTotal || 1
    return data.providersWorkingByPeriod.buckets.map((row) => {
      if (providersView === 'Count') {
        return {
          month: row.label,
          barValue: row.value,
          topLabel: row.value.toFixed(1),
        }
      }
      const pct = Math.round((row.value / total) * 1000) / 10
      return {
        month: row.label,
        barValue: pct,
        topLabel: `${pct}%`,
      }
    })
  }, [data, providersView, providersTotal])

  const providersYAxis = useMemo(() => {
    if (providersView === 'Percentage') {
      return computePercentAxis(providersChartData.map((row) => row.barValue))
    }
    return computeDynamicCountAxis(
      providersChartData.map((row) => row.barValue),
    )
  }, [providersView, providersChartData])

  const utilizationChartData = useMemo(() => {
    if (!data) return []
    return data.utilizationRateByPeriod.buckets.map((row) => ({
      month: row.label,
      barValue: row.value,
      topLabel: `${row.value}%`,
    }))
  }, [data])

  const utilizationYAxis = useMemo(
    () => computePercentAxis(utilizationChartData.map((row) => row.barValue)),
    [utilizationChartData],
  )

  const kpiEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      data?.chairTimeUsed.value,
      data?.chairTimeAvailable.value,
      data?.avgRoomsUsed.value,
      data?.avgRoomOperations.value,
      data?.utilizationRate.value,
    ),
  )
  const chairTimeEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.chairTimeUsedByPeriod?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ) ||
      (data?.chairTimeAvailableByPeriod?.buckets ?? []).some((row) =>
        hasNonZeroDashboardValues(row.value),
      ),
  )
  const roomUtilEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.roomsUsedByPeriod?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ) ||
      (data?.roomsAvailableByPeriod?.buckets ?? []).some((row) =>
        hasNonZeroDashboardValues(row.value),
      ),
  )
  const providersEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.providersWorkingByPeriod?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const utilizationEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.utilizationRateByPeriod?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )

  return (
    <div className="reports-patient-base-health reports-treatment-conversion-funnel reports-chair-room-utilization">
      <ReportBundleStickyTopWhenExpanded>
        <ReportPageHeader title="Chair & Room Utilization">
          {reportSaveViewAction}
          {reportGeneratePDFAction}
        </ReportPageHeader>

        <ReportFilterToolbar
          filters={CHAIR_ROOM_UTILIZATION_FILTERS}
          onReportSaveViewActionChange={setReportSaveViewAction}
          onReportGeneratePDFActionChange={setReportGeneratePDFAction}
          onReportFiltersAndMetricsChange={setReportFiltersAndMetrics}
        />
      </ReportBundleStickyTopWhenExpanded>

      {kpiEmpty ? (
        <DashboardSectionEmptyState />
      ) : (
        <ReportKpiGrid items={kpiItems} />
      )}

      <div className="reports-treatment-conversion-funnel__stack">
        <div className="reports-treatment-conversion-funnel__grid">
          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Chairs - Time Used vs Available
                </ReportSectionHeading>
                {chairTimeEmpty ? null : (
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={chairTimeView}
                    onChange={(value) =>
                      setChairTimeView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              {chairTimeEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={chairTimeChartData}
                      margin={REPORT_BAR_CHART_MARGIN_LEGEND}
                      barSize={50}
                      barGap={REPORT_GROUPED_BAR_GAP}
                      barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                      />
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                      />
                      <YAxis
                        domain={chairTimeYAxis.domain}
                        ticks={chairTimeYAxis.ticks}
                        tickFormatter={chairTimeYAxis.tickFormatter}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                      />
                      <Tooltip
                        formatter={(value: number) =>
                          chairTimeView === 'Count'
                            ? formatMinutes(value)
                            : `${value}%`
                        }
                        contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                      />
                      <Legend
                        iconType="circle"
                        iconSize={8}
                        wrapperStyle={REPORT_LEGEND_WRAPPER_STYLE}
                      />
                      <Bar
                        dataKey="usedValue"
                        name="Time Used"
                        fill="var(--blue-400)"
                        radius={[6, 6, 6, 6]}
                      />
                      <Bar
                        dataKey="availableValue"
                        name="Available"
                        fill="var(--orange-300)"
                        radius={[6, 6, 6, 6]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </ReportSectionCard>

          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>Room Utilization</ReportSectionHeading>
                {roomUtilEmpty ? null : (
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={roomUtilView}
                    onChange={(value) =>
                      setRoomUtilView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              {roomUtilEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={roomUtilChartData}
                      margin={REPORT_BAR_CHART_MARGIN_LEGEND}
                      barSize={50}
                      barGap={REPORT_GROUPED_BAR_GAP}
                      barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                      />
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                      />
                      <YAxis
                        domain={roomUtilYAxis.domain}
                        ticks={roomUtilYAxis.ticks}
                        tickFormatter={roomUtilYAxis.tickFormatter}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                      />
                      <Tooltip
                        formatter={(value: number) =>
                          roomUtilView === 'Count' ? value : `${value}%`
                        }
                        contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                      />
                      <Legend
                        iconType="circle"
                        iconSize={8}
                        wrapperStyle={REPORT_LEGEND_WRAPPER_STYLE}
                      />
                      <Bar
                        dataKey="usedValue"
                        name="Avg. Used Rooms"
                        fill="var(--blue-400)"
                        radius={[6, 6, 6, 6]}
                      />
                      <Bar
                        dataKey="availableValue"
                        name="Avg. Available Rooms"
                        fill="var(--orange-300)"
                        radius={[6, 6, 6, 6]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </ReportSectionCard>
        </div>

        <div className="reports-treatment-conversion-funnel__grid">
          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>Providers Working</ReportSectionHeading>
                {providersEmpty ? null : (
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={providersView}
                    onChange={(value) =>
                      setProvidersView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              <p className="reports-patient-value-revenue__note">
                Avg. distinct providers working grouped by {bucketLabel}
              </p>
              {providersEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={providersChartData}
                      margin={REPORT_BAR_CHART_MARGIN}
                      barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                      />
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                      />
                      <YAxis
                        domain={providersYAxis.domain}
                        ticks={providersYAxis.ticks}
                        tickFormatter={providersYAxis.tickFormatter}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                      />
                      <Tooltip
                        formatter={(value: number) =>
                          providersView === 'Count' ? value : `${value}%`
                        }
                        contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                      />
                      <Bar
                        dataKey="barValue"
                        fill="var(--blue-400)"
                        radius={[8, 8, 8, 8]}
                        barSize={100}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </ReportSectionCard>

          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Operations Efficiency - Utilization Rate
                </ReportSectionHeading>
              </div>
              {utilizationEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={utilizationChartData}
                      margin={REPORT_BAR_CHART_MARGIN}
                      barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                      />
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                      />
                      <YAxis
                        domain={utilizationYAxis.domain}
                        ticks={utilizationYAxis.ticks}
                        tickFormatter={utilizationYAxis.tickFormatter}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                      />
                      <Tooltip
                        formatter={(value: number) => `${value}%`}
                        contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                      />
                      <Bar
                        dataKey="barValue"
                        fill="var(--blue-400)"
                        radius={[8, 8, 8, 8]}
                        barSize={100}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </ReportSectionCard>
        </div>
      </div>
    </div>
  )
}

export default ChairRoomUtilizationReport
